import { Pencil, Save, X } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { FlagBadge } from './FlagBadge';
import { injectTSDI as tsdi } from '../../../../core/tsdi';
import { Api } from '../../../../core/api';
import {
    WorkoutFlagStatus,
    type UserFlagSummaryDto
} from '../../../../core/api/FitnessActivityClient';

interface UserFlagBannerProps {
    userFlag: UserFlagSummaryDto | null | undefined;
    onFlagUpdated: () => void;
}

const statusOptions = Object.values(WorkoutFlagStatus).map((status) => ({
    label: status,
    value: status
}));

const alertVariants: Record<
    string,
    'destructive' | 'warning' | 'success' | 'info'
> = {
    [WorkoutFlagStatus.FRAUD]: 'destructive',
    [WorkoutFlagStatus.SUSPICIOUS]: 'warning',
    [WorkoutFlagStatus.NOT_FRAUD]: 'success'
};

export const UserFlagBanner = observer(
    ({ userFlag, onFlagUpdated }: UserFlagBannerProps) => {
        const { fitnessActivityClient } = tsdi(Api);
        const [editing, setEditing] = useState(false);
        const [editStatus, setEditStatus] = useState<WorkoutFlagStatus>(
            WorkoutFlagStatus.SUSPICIOUS
        );
        const [editComment, setEditComment] = useState('');
        const [saving, setSaving] = useState(false);

        if (!userFlag) return null;

        const startEdit = () => {
            setEditStatus(userFlag.status);
            setEditComment(userFlag.comment);
            setEditing(true);
        };

        const cancelEdit = () => {
            setEditing(false);
        };

        const saveEdit = async () => {
            setSaving(true);
            try {
                await fitnessActivityClient.admin.userFlagControllerUpdateUserFlag(
                    userFlag.id,
                    {
                        id: userFlag.id,
                        userId: '',
                        status: editStatus,
                        comment: editComment,
                        createdBy: userFlag.createdBy,
                        activityIds: []
                    }
                );
                setEditing(false);
                onFlagUpdated();
            } finally {
                setSaving(false);
            }
        };

        return (
            <Alert variant={alertVariants[userFlag.status] ?? 'info'}>
                <AlertDescription className="w-full">
                    {!editing ? (
                        <div className="flex w-full items-center justify-between gap-4">
                            <div className="flex flex-1 flex-wrap items-center gap-4">
                                <FlagBadge status={userFlag.status} />
                                <span className="text-sm text-foreground">
                                    {userFlag.comment || 'No comment'}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    by {userFlag.createdBy} ·{' '}
                                    {userFlag.flaggedActivityCount} flagged{' '}
                                    {userFlag.flaggedActivityCount === 1
                                        ? 'activity'
                                        : 'activities'}
                                </span>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={startEdit}
                                title="Edit user flag"
                            >
                                <Pencil />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex w-full flex-wrap items-end gap-3">
                            <Select
                                label="Status"
                                value={editStatus}
                                options={statusOptions}
                                fullWidth={false}
                                onChange={(value: WorkoutFlagStatus) =>
                                    setEditStatus(value)
                                }
                            />
                            <TextField
                                className="min-w-[240px] flex-1"
                                label="Comment"
                                type="text"
                                multiline
                                maxRows={3}
                                value={editComment}
                                onChange={(value) =>
                                    setEditComment(String(value))
                                }
                            />
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={saveEdit}
                                disabled={saving}
                                title="Save"
                                className="text-success"
                            >
                                <Save />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={cancelEdit}
                                disabled={saving}
                                title="Cancel"
                            >
                                <X />
                            </Button>
                        </div>
                    )}
                </AlertDescription>
            </Alert>
        );
    }
);
